import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from './i18n/LanguageContext';

/**
 * Full-screen lockout page for suspended accounts.
 * Shows reason + lift date + sign-out option (suspended users keep their data,
 * just can't access during the suspension window).
 */
const AccountSuspended = ({ isDarkMode = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = sessionStorage.getItem('lockout_info');
    if (stored) {
      try {
        const info = JSON.parse(stored);
        if (info.code === 'suspended') {
          setReason(info.reason || '');
          setUntil(info.until || '');
          setLoading(false);
          return;
        }
      } catch {}
    }
    const email = sessionStorage.getItem('lockout_email') || localStorage.getItem('userEmail') || '';
    if (!email) {
      navigate('/');
      return;
    }
    const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
    fetch(`${API}/auth/account-lockout-info?email=${encodeURIComponent(email)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.code === 'suspended') {
          setReason(data.reason || '');
          setUntil(data.until || '');
        } else {
          navigate('/');
        }
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const formatUntil = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
      });
    } catch { return ''; }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? '#111827' : '#f9fafb' }}>
        <div style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}>{t('loading_ellipsis')}</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: isDarkMode ? '#111827' : '#f9fafb',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        maxWidth: 520,
        width: '100%',
        background: isDarkMode ? '#1f2937' : '#ffffff',
        borderRadius: 16,
        padding: '40px 32px',
        textAlign: 'center',
        boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
        border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
      }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>⏸️</div>
        <h1 style={{
          fontSize: 28,
          fontWeight: 700,
          margin: '0 0 16px 0',
          color: isDarkMode ? '#fef3c7' : '#92400e',
        }}>{t('account_suspended_title')}</h1>

        {reason && (
          <div style={{
            background: isDarkMode ? '#78350f' : '#fffbeb',
            border: `1px solid ${isDarkMode ? '#92400e' : '#fde68a'}`,
            borderRadius: 10,
            padding: '14px 18px',
            margin: '20px 0',
            textAlign: 'left',
          }}>
            <div style={{
              fontSize: 12,
              fontWeight: 600,
              color: isDarkMode ? '#fde68a' : '#92400e',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 4,
            }}>{t('reason_label')}</div>
            <div style={{ fontSize: 15, color: isDarkMode ? '#fef3c7' : '#78350f', lineHeight: 1.5 }}>
              {reason}
            </div>
          </div>
        )}

        {until && (
          <div style={{
            background: isDarkMode ? '#1e3a8a' : '#eff6ff',
            border: `1px solid ${isDarkMode ? '#1e40af' : '#bfdbfe'}`,
            borderRadius: 10,
            padding: '14px 18px',
            margin: '20px 0',
            textAlign: 'left',
          }}>
            <div style={{
              fontSize: 12,
              fontWeight: 600,
              color: isDarkMode ? '#bfdbfe' : '#1e40af',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 4,
            }}>{t('account_suspended_lifts_on')}</div>
            <div style={{ fontSize: 15, color: isDarkMode ? '#dbeafe' : '#1e3a8a', lineHeight: 1.5 }}>
              {formatUntil(until)}
            </div>
          </div>
        )}

        <p style={{
          fontSize: 14,
          color: isDarkMode ? '#9ca3af' : '#6b7280',
          lineHeight: 1.6,
          margin: '20px 0 28px 0',
        }}>
          {t('account_suspended_explanation')}
        </p>

        <a
          href="mailto:support@darijapro.com"
          style={{
            display: 'inline-block',
            background: '#f59e0b',
            color: '#ffffff',
            padding: '12px 32px',
            borderRadius: 8,
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          ✉️ {t('account_contact_support')}
        </a>
      </div>
    </div>
  );
};

export default AccountSuspended;
